import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  ChevronRight,
  Factory,
  ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const FIELD_MAPPINGS = [
  // Required fields - highlighted
  { key: 'consignee', label: 'Consignee/Factory Name', required: true, group: 'required' },
  { key: 'consignee_country', label: 'Factory Country (Where factory is located)', required: true, group: 'required' },
  { key: 'hs_code', label: 'HSN Code', required: true, group: 'required' },
  { key: 'import_country', label: 'Import Country (Origin of goods)', required: true, group: 'required' },
  // Optional fields
  { key: 'shipper', label: 'Shipper/Supplier Name', required: false, group: 'optional' },
  { key: 'product_description', label: 'Product Description', required: false, group: 'optional' },
  { key: 'quantity', label: 'Quantity', required: false, group: 'optional' },
  { key: 'unit', label: 'Unit', required: false, group: 'optional' },
  { key: 'currency', label: 'Currency', required: false, group: 'optional' },
  { key: 'origin_port', label: 'Origin Port', required: false, group: 'optional' },
  { key: 'destination_port', label: 'Destination Port', required: false, group: 'optional' },
  { key: 'bill_of_lading', label: 'Bill of Lading', required: false, group: 'optional' },
];

const ExportImports = () => {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedImport, setSelectedImport] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [showMappingDialog, setShowMappingDialog] = useState(false);

  useEffect(() => {
    fetchImports();
  }, []);

  const fetchImports = async () => {
    try {
      const response = await axios.get(`${API}/export/imports`);
      setImports(response.data);
    } catch (error) {
      toast.error("Failed to load imports");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await axios.post(`${API}/export/imports/upload`, formData);
      toast.success("File uploaded successfully");
      setSelectedImport(response.data);
      
      // Auto-map columns if possible
      const autoMapping = {};
      const columns = response.data.columns || [];
      columns.forEach(col => {
        const colLower = col.toLowerCase();
        // Required fields
        if (colLower.includes('consignee') || colLower.includes('importer')) autoMapping.consignee = col;
        else if ((colLower.includes('consignee') && colLower.includes('country')) || 
                 (colLower.includes('importer') && colLower.includes('country')) ||
                 colLower === 'country') autoMapping.consignee_country = col;
        else if (colLower.includes('hs') && (colLower.includes('code') || colLower.includes('no'))) autoMapping.hs_code = col;
        else if (colLower.includes('hsn')) autoMapping.hs_code = col;
        else if ((colLower.includes('origin') && colLower.includes('country')) ||
                 (colLower.includes('exporter') && colLower.includes('country')) ||
                 colLower.includes('import from')) autoMapping.import_country = col;
        // Optional fields
        else if (colLower.includes('shipper') || colLower.includes('exporter') || colLower.includes('supplier')) autoMapping.shipper = col;
        else if (colLower.includes('product') || colLower.includes('description') || colLower.includes('item')) autoMapping.product_description = col;
        else if (colLower.includes('quantity') || colLower === 'qty') autoMapping.quantity = col;
        else if (colLower.includes('unit') && !colLower.includes('quantity')) autoMapping.unit = col;
        else if (colLower.includes('currency')) autoMapping.currency = col;
        else if (colLower.includes('indian') && colLower.includes('port')) autoMapping.origin_port = col;
        else if (colLower.includes('foreign') && colLower.includes('port')) autoMapping.destination_port = col;
        else if (colLower.includes('bill') || colLower.includes('lading') || colLower.includes('bl')) autoMapping.bill_of_lading = col;
      });
      setColumnMapping(autoMapping);
      setShowMappingDialog(true);
      fetchImports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleProcessImport = async () => {
    // Check all required fields
    const requiredFields = FIELD_MAPPINGS.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !columnMapping[f.key]);
    
    if (missingFields.length > 0) {
      toast.error(`Please map required fields: ${missingFields.map(f => f.label.split(' (')[0]).join(', ')}`);
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/export/imports/${selectedImport.id}/process`,
        columnMapping
      );
      toast.success(`Processed ${response.data.shipments_created} shipments, created ${response.data.factories_created} factories`);
      setShowMappingDialog(false);
      setSelectedImport(null);
      setColumnMapping({});
      fetchImports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Processing failed");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: { color: "bg-green-100 text-green-800", icon: CheckCircle2 },
      error: { color: "bg-red-100 text-red-800", icon: XCircle },
      processing: { color: "bg-blue-100 text-blue-800", icon: Loader2 },
      pending: { color: "bg-yellow-100 text-yellow-800", icon: Clock }
    };
    const v = variants[status] || variants.pending;
    const Icon = v.icon;
    return (
      <Badge variant="outline" className={`${v.color} gap-1`}>
        <Icon className={`w-3 h-3 ${status === 'processing' ? 'animate-spin' : ''}`} />
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="export-imports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Shipment Imports</h1>
          <p className="text-slate-500">Upload and process shipment data files</p>
        </div>
        <label className="cursor-pointer">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
          <Button className="gap-2 bg-blue-600 hover:bg-blue-700" disabled={uploading} asChild>
            <span>
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload File
                </>
              )}
            </span>
          </Button>
        </label>
      </div>

      {/* Instructions */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Supported Formats</p>
              <p className="text-sm text-blue-700">
                Excel (.xlsx, .xls) and CSV files. Required field: <strong>Consignee/Factory Name</strong>.
                The system will automatically create factory profiles from unique consignee names.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Imports Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Import History</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : imports.length === 0 ? (
            <div className="text-center py-12">
              <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No imports yet. Upload a shipment file to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rows</TableHead>
                  <TableHead>Factories</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {imports.map((imp) => (
                  <TableRow key={imp.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{imp.original_filename}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(imp.status)}</TableCell>
                    <TableCell>{imp.row_count || 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Factory className="w-3 h-3 text-slate-400" />
                        {imp.factory_count || 0}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(imp.uploaded_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {imp.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          onClick={() => {
                            setSelectedImport(imp);
                            setColumnMapping({});
                            setShowMappingDialog(true);
                          }}
                        >
                          Configure
                          <ChevronRight className="w-3 h-3" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Column Mapping Dialog */}
      <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Map Columns</DialogTitle>
            <DialogDescription>
              Map your file columns to the system fields. All required fields must be mapped.
            </DialogDescription>
          </DialogHeader>

          {selectedImport && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">
                  <strong>File:</strong> {selectedImport.original_filename}
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Rows:</strong> {selectedImport.row_count}
                </p>
              </div>

              {/* Required Fields Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-2 border-b border-red-200">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <h4 className="text-sm font-semibold text-red-700">Required Fields</h4>
                </div>
                {FIELD_MAPPINGS.filter(f => f.required).map((field) => (
                  <div key={field.key} className="grid grid-cols-2 gap-4 items-center bg-red-50/50 p-2 rounded-lg border border-red-100">
                    <Label className="font-medium text-slate-800">
                      {field.label} <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={columnMapping[field.key] || 'skip'}
                      onValueChange={(value) => setColumnMapping({
                        ...columnMapping,
                        [field.key]: value === 'skip' ? undefined : value
                      })}
                    >
                      <SelectTrigger className={!columnMapping[field.key] ? 'border-red-300' : 'border-green-300'}>
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="skip">-- Select Column --</SelectItem>
                        {(selectedImport.columns || []).map((col) => (
                          <SelectItem key={col} value={col}>{col}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {/* Optional Fields Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                  <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                  <h4 className="text-sm font-semibold text-slate-600">Optional Fields</h4>
                </div>
                {FIELD_MAPPINGS.filter(f => !f.required).map((field) => (
                  <div key={field.key} className="grid grid-cols-2 gap-4 items-center">
                    <Label className="text-slate-600">
                      {field.label}
                    </Label>
                    <Select
                      value={columnMapping[field.key] || 'skip'}
                      onValueChange={(value) => setColumnMapping({
                        ...columnMapping,
                        [field.key]: value === 'skip' ? undefined : value
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="skip">-- Skip --</SelectItem>
                        {(selectedImport.columns || []).map((col) => (
                          <SelectItem key={col} value={col}>{col}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {/* Sample Data Preview */}
              {selectedImport.sample_data && selectedImport.sample_data.length > 0 && (
                <div className="mt-4">
                  <Label className="mb-2 block">Sample Data Preview</Label>
                  <div className="overflow-x-auto">
                    <table className="text-xs w-full border border-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          {(selectedImport.columns || []).slice(0, 6).map((col) => (
                            <th key={col} className="px-2 py-1 text-left border-b">{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {selectedImport.sample_data.slice(0, 3).map((row, idx) => (
                          <tr key={idx}>
                            {(selectedImport.columns || []).slice(0, 6).map((col) => (
                              <td key={col} className="px-2 py-1 border-b truncate max-w-[150px]">
                                {String(row[col] || '').slice(0, 50)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMappingDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleProcessImport}
              disabled={processing || !columnMapping.consignee}
              className="gap-2 bg-blue-600 hover:bg-blue-700"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4" />
                  Process Import
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExportImports;
